'use client';

import { Check, Download, Edit2, FileIcon, Maximize2, Trash2, X } from 'lucide-react';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { cn } from '../lib/utils';
import type { EditorAttachment } from '../types/files';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { VisuallyHidden } from './visually-hidden';

export type AttachmentListLayout = 'compact' | 'list' | 'grid';

export interface AttachmentListProps {
  attachments?: EditorAttachment[];
  onRemove?: (index: number) => void;
  onRename?: (index: number, newName: string) => void;
  className?: string;
  layout?: AttachmentListLayout;
}

export function AttachmentList({
  attachments = [],
  onRemove,
  onRename,
  className,
  layout = 'compact',
}: AttachmentListProps) {
  if (attachments.length === 0) return null;

  return (
    <div
      className={cn(
        layout === 'list'
          ? 'flex flex-col gap-2'
          : layout === 'grid'
            ? 'grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4'
            : 'flex flex-wrap gap-3 border-t border-border bg-muted/5 px-3 py-3',
        className
      )}
    >
      {attachments.map((attachment, i) => (
        <AttachmentItem
          key={attachment.type === 'local' ? attachment.id : `${attachment.fileId}-${i}`}
          attachment={attachment}
          layout={layout}
          onRemove={onRemove ? () => onRemove(i) : undefined}
          onRename={onRename ? (newName) => onRename(i, newName) : undefined}
        />
      ))}
    </div>
  );
}

function AttachmentItem({
  attachment,
  layout,
  onRemove,
  onRename,
}: {
  attachment: EditorAttachment;
  layout: AttachmentListLayout;
  onRemove?: () => void;
  onRename?: (newName: string) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [newName, setNewName] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const localObjectUrl = useMemo(() => {
    if (attachment.type === 'local' && attachment.file.type.startsWith('image/')) {
      return URL.createObjectURL(attachment.file);
    }
    return null;
  }, [attachment]);

  useEffect(() => {
    return () => {
      if (localObjectUrl) URL.revokeObjectURL(localObjectUrl);
    };
  }, [localObjectUrl]);

  const previewUri =
    localObjectUrl ??
    (attachment.type === 'remote' && attachment.fileType.startsWith('image/')
      ? attachment.viewUrl || null
      : null);

  const fileName = attachment.type === 'local' ? attachment.file.name : attachment.fileName;
  const displayName = fileName;
  const isImage = (
    attachment.type === 'local' ? attachment.file.type : attachment.fileType
  ).startsWith('image/');

  const handleDownload = () => {
    const a = document.createElement('a');
    if (attachment.type === 'local') {
      const url = URL.createObjectURL(attachment.file);
      a.href = url;
      a.download = attachment.file.name;
      a.click();
      URL.revokeObjectURL(url);
    } else if (attachment.viewUrl) {
      a.href = attachment.viewUrl;

      // Ensure extension is preserved in download name
      let downloadName = displayName;
      const extension = fileName.split('.').pop();
      if (extension && !downloadName.toLowerCase().endsWith(`.${extension.toLowerCase()}`)) {
        downloadName = `${downloadName}.${extension}`;
      }

      a.download = downloadName;
      a.target = '_blank';
      a.click();
    }
  };

  const handleRenameSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (newName.trim() && newName !== displayName) {
      onRename?.(newName.trim());
    }
    setIsEditing(false);
  };

  const managedLayout = layout !== 'compact';

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <div
        className={cn(
          'group relative flex overflow-visible rounded-lg border border-border bg-background shadow-sm transition-colors hover:border-primary/50',
          layout === 'compact' && 'size-14 shrink-0 items-center justify-center',
          layout === 'list' && 'w-full items-center gap-3 p-2',
          layout === 'grid' && 'flex-col'
        )}
      >
        <DialogTrigger asChild>
          <button
            type="button"
            className={cn(
              'group/btn relative overflow-hidden rounded-md border-none bg-transparent p-0',
              layout === 'compact' && 'size-full',
              layout === 'list' && 'size-10 shrink-0',
              layout === 'grid' && 'aspect-square w-full'
            )}
          >
            {previewUri ? (
              <img src={previewUri} alt={displayName} className="size-full object-cover" />
            ) : (
              <div className="flex size-full flex-col items-center justify-center gap-0.5 p-1 text-muted-foreground/60">
                <FileIcon className={cn('shrink-0', managedLayout ? 'size-7' : 'size-5')} />
                {layout === 'compact' && (
                  <span className="w-full truncate px-1 text-center text-[8px] font-medium leading-tight">
                    {displayName.split('.').pop()?.toUpperCase()}
                  </span>
                )}
              </div>
            )}
            <div className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-colors group-hover/btn:bg-black/20 group-hover/btn:opacity-100">
              <div className="rounded-full bg-white/20 p-1.5 text-white backdrop-blur-md">
                <Maximize2 className="size-3" />
              </div>
            </div>
          </button>
        </DialogTrigger>

        {managedLayout && (
          <div className={cn('min-w-0 flex-1', layout === 'grid' && 'w-full px-3 pb-3')}>
            <p className="truncate text-sm font-medium" title={displayName}>
              {displayName}
            </p>
            <p className="truncate text-xs text-muted-foreground">
              {attachment.type === 'local' ? attachment.file.type : attachment.fileType}
            </p>
          </div>
        )}

        {onRemove && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            className="absolute -top-1.5 -right-1.5 z-10 rounded-full border border-border bg-background p-0.5 text-muted-foreground opacity-0 shadow-sm transition-all hover:border-destructive hover:bg-destructive hover:text-destructive-foreground focus:opacity-100 group-hover:opacity-100"
            title="Remove attachment"
          >
            <X className="size-3" />
          </button>
        )}

        {attachment.type === 'remote' && (
          <div className="absolute -bottom-1 -left-1 flex size-3 items-center justify-center rounded-full border border-background bg-primary shadow-sm">
            <div className="size-1 rounded-full bg-white" />
          </div>
        )}
      </div>

      <DialogContent className="max-w-[90vw] sm:max-w-[600px] p-0 overflow-hidden gap-0">
        <DialogHeader className="p-4 border-b border-border bg-muted/30">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              {isEditing ? (
                <form onSubmit={handleRenameSubmit} className="flex items-center gap-2">
                  <Input
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    className="h-8 py-0 px-2 text-sm focus-visible:ring-1"
                    autoFocus
                    onBlur={() => handleRenameSubmit()}
                  />
                  <Button type="submit" size="sm" variant="ghost" className="h-8 w-8 p-0">
                    <Check className="size-4 text-green-500" />
                  </Button>
                </form>
              ) : (
                <div className="flex items-center gap-2 group/title">
                  <DialogTitle className="text-sm font-semibold truncate max-w-[300px]">
                    {displayName}
                  </DialogTitle>
                  {onRename && (
                    <button
                      onClick={() => {
                        setNewName(displayName);
                        setIsEditing(true);
                      }}
                      className="opacity-0 group-hover/title:opacity-100 transition-opacity p-1 hover:bg-muted rounded"
                    >
                      <Edit2 className="size-3 text-muted-foreground" />
                    </button>
                  )}
                </div>
              )}
              <DialogDescription className="text-[10px] text-muted-foreground uppercase tracking-tight font-medium mt-0.5">
                {attachment.type === 'local' ? 'Local File' : 'Stored in Cloud'} •{' '}
                {attachment.type === 'local'
                  ? `${Math.round(attachment.file.size / 1024)} KB`
                  : attachment.fileType}
              </DialogDescription>
            </div>
            <VisuallyHidden>
              <DialogTitle>{displayName}</DialogTitle>
              <DialogDescription>Viewing file {displayName}</DialogDescription>
            </VisuallyHidden>
          </div>
        </DialogHeader>

        <div className="flex items-center justify-center bg-muted/10 p-6 min-h-[300px] max-h-[70vh] overflow-auto">
          {isImage && previewUri ? (
            <img
              src={previewUri}
              alt={displayName}
              className="max-w-full max-h-full object-contain rounded-lg shadow-lg border border-border"
            />
          ) : (
            <div className="flex flex-col items-center gap-4 text-muted-foreground py-12">
              <div className="size-20 rounded-2xl bg-muted flex items-center justify-center border border-border shadow-inner">
                <FileIcon className="size-10" />
              </div>
              <div className="text-center space-y-1">
                <p className="text-sm font-medium text-foreground">No preview available</p>
                <p className="text-xs">You can still download this file to view it.</p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="p-3 border-t border-border bg-muted/30 sm:justify-between flex-row items-center gap-2">
          <div className="flex items-center gap-2">
            {onRemove && (
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive hover:bg-destructive/10 gap-2 h-9"
                onClick={() => {
                  onRemove();
                  setIsOpen(false);
                }}
              >
                <Trash2 className="size-4" />
                <span className="hidden sm:inline">Delete File</span>
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="gap-2 h-9"
              onClick={() => setIsOpen(false)}
            >
              Close
            </Button>
            <Button size="sm" className="gap-2 h-9 shadow-sm" onClick={handleDownload}>
              <Download className="size-4" />
              Download
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
